// src/pages/Financial/index.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  IconButton,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Stack
} from '@mui/material';
import DevOnly from '../../components/DevOnly';
import {
  TrendingUp,
  AttachMoney as MoneyIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  RequestQuote as QuoteIcon,
  Percent as DiscountIcon,
  CardGiftcard as VoucherIcon,
  LocalOffer,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Description as DocumentIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';
import MetricCard from '../../components/dashboard/MetricCard';
import { alpha, useTheme } from '@mui/material/styles';

const FinancialDashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('all_time');
  const [glanceTab, setGlanceTab] = useState('invoices');
  const overviewFallback = {
    metrics: {
      revenue: { value: 0, trend: 'up', change: '0%' },
      profit: { value: 0, trend: 'up', change: '0%' },
      outstanding: { value: 0, trend: 'down', change: '0%' },
      avgInvoice: { value: 0, trend: 'up', change: '0%' }
    },
    counts: {
      invoices: { total: 0, active: 0 },
      quotes: { total: 0, active: 0 },
      payments: { total: 0 },
      vouchers: { total: 0, active: 0 },
      discounts: { total: 0, active: 0 }
    },
    recentInvoices: [],
    recentPayments: [],
    recentQuotes: [],
    recentVouchers: [],
    recentDiscounts: []
  };

  // Fetch financial overview data
  const { data: financialData, isLoading: financialLoading, error: financialError } = useQuery({
    queryKey: ['financial-overview', period],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/financial/overview', {
          params: { period }
        });
        return response.data;
      } catch (error) {
        console.error('Error fetching financial data:', error);
        return overviewFallback;
      }
    },
    staleTime: 2 * 60 * 1000,
  });

  const safeData = financialData ?? overviewFallback;
  const metrics = safeData.metrics ?? overviewFallback.metrics;
  const entityCounts = safeData.counts ?? overviewFallback.counts;
  const recentInvoices = safeData.recentInvoices ?? [];
  const recentPayments = safeData.recentPayments ?? [];
  const recentQuotes = safeData.recentQuotes ?? [];
  const recentVouchers = safeData.recentVouchers ?? [];
  const recentDiscounts = safeData.recentDiscounts ?? [];
  const isOverviewLoading = financialLoading && !financialData;

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handleGlanceTabChange = (_event, newValue) => {
    setGlanceTab(newValue);
  };

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString();
  };

  // No client-side search on recent items per feedback

  if (financialError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load financial data. Please try again later.
        </Alert>
      </Box>
    );
  }


  const summaryCards = [
    {
      key: 'invoices',
      title: 'Invoices',
      total: entityCounts?.invoices?.total ?? 0,
      subtitle: `${entityCounts?.invoices?.active ?? 0} active`,
      icon: InvoiceIcon,
      accent: 'primary'
    },
    {
      key: 'quotes',
      title: 'Quotes',
      total: entityCounts?.quotes?.total ?? 0,
      subtitle: `${entityCounts?.quotes?.active ?? 0} pending`,
      icon: QuoteIcon,
      accent: 'info'
    },
    {
      key: 'payments',
      title: 'Payments',
      total: entityCounts?.payments?.total ?? 0,
      subtitle: 'Received payments',
      icon: PaymentIcon,
      accent: 'success'
    },
    {
      key: 'vouchers',
      title: 'Gift Vouchers',
      total: entityCounts?.vouchers?.total ?? 0,
      subtitle: `${entityCounts?.vouchers?.active ?? 0} active`,
      icon: VoucherIcon,
      accent: 'secondary'
    },
    {
      key: 'discounts',
      title: 'Discounts',
      total: entityCounts?.discounts?.total ?? 0,
      subtitle: `${entityCounts?.discounts?.active ?? 0} active`,
      icon: LocalOffer,
      accent: 'warning'
    }
  ];

  const glanceTabsConfig = [
    {
      value: 'invoices',
      label: 'Invoices',
      icon: InvoiceIcon,
      route: '/financial/invoices',
      description: 'Latest invoices that were created or updated recently.',
      actionLabel: 'Open Invoices',
      getStats: (counts) => [
        { label: 'Total', value: counts?.invoices?.total ?? 0, color: 'primary' },
        { label: 'Active', value: counts?.invoices?.active ?? 0, color: 'success' }
      ],
      secondaryAction: {
        label: 'Create Invoice',
        path: '/financial/invoices/create',
        icon: AddIcon
      }
    },
    {
      value: 'payments',
      label: 'Payments',
      icon: PaymentIcon,
      route: '/financial/payments',
      description: 'Payments received across channels.',
      actionLabel: 'Open Payments',
      getStats: (counts) => [
        { label: 'Total', value: counts?.payments?.total ?? 0, color: 'success' }
      ]
    },
    {
      value: 'quotes',
      label: 'Quotes',
      icon: QuoteIcon,
      route: '/financial/quotes',
      description: 'Quotes that may need follow up or approval.',
      actionLabel: 'Open Quotes',
      getStats: (counts) => [
        { label: 'Total', value: counts?.quotes?.total ?? 0, color: 'info' },
        { label: 'Pending', value: counts?.quotes?.active ?? 0, color: 'warning' }
      ]
    },
    {
      value: 'discounts',
      label: 'Discounts',
      icon: DiscountIcon,
      route: '/discounts',
      description: 'Monitor discount codes and their current status.',
      actionLabel: 'Manage Discounts',
      getStats: (counts) => [
        { label: 'Total', value: counts?.discounts?.total ?? 0, color: 'primary' },
        { label: 'Active', value: counts?.discounts?.active ?? 0, color: 'success' }
      ]
    },
    {
      value: 'vouchers',
      label: 'Vouchers',
      icon: VoucherIcon,
      route: '/vouchers',
      description: 'Recent gift voucher activity and availability.',
      actionLabel: 'Manage Vouchers',
      getStats: (counts) => [
        { label: 'Total', value: counts?.vouchers?.total ?? 0, color: 'info' },
        { label: 'Active', value: counts?.vouchers?.active ?? 0, color: 'secondary' }
      ]
    }
  ];

  const activeTabConfig =
    glanceTabsConfig.find((tab) => tab.value === glanceTab) ?? glanceTabsConfig[0];
  const activeTabStats = activeTabConfig?.getStats?.(entityCounts) ?? [];
  const hasSecondaryAction = Boolean(activeTabConfig?.secondaryAction);
  const SecondaryActionIcon = hasSecondaryAction
    ? activeTabConfig.secondaryAction.icon || AddIcon
    : null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Financial Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track revenue, manage invoices, and monitor financial performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={period}
              label="Period"
              onChange={handlePeriodChange}
            >
              <MenuItem value="all_time">All Time</MenuItem>
              <MenuItem value="this_week">This Week</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="this_quarter">This Quarter</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
            </Select>
          </FormControl>
          <DevOnly>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/financial/invoices/create')}
              size="large"
            >
              Create Invoice
            </Button>
          </DevOnly>
        </Box>
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Revenue"
            value={metrics.revenue.value}
            trend={metrics.revenue.trend}
            trendValue={metrics.revenue.change}
            color="primary"
            format="currency"
            icon={<MoneyIcon />}
            loading={financialLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Net Profit"
            value={metrics.profit.value}
            trend={metrics.profit.trend}
            trendValue={metrics.profit.change}
            color="success"
            format="currency"
            icon={<TrendingUp />}
            loading={financialLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Outstanding"
            value={metrics.outstanding.value}
            trend={metrics.outstanding.trend}
            trendValue={metrics.outstanding.change}
            color="warning"
            format="currency"
            icon={<PaymentIcon />}
            loading={financialLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Invoice"
            value={metrics.avgInvoice.value}
            trend={metrics.avgInvoice.trend}
            trendValue={metrics.avgInvoice.change}
            color="info"
            format="currency"
            icon={<InvoiceIcon />}
            loading={financialLoading}
          />
        </Grid>
      </Grid>

      {/* Entity Summary */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.key}>
            <SummaryCard
              title={card.title}
              total={card.total}
              subtitle={card.subtitle}
              icon={card.icon}
              accent={card.accent}
              loading={isOverviewLoading}
            />
          </Grid>
        ))}
      </Grid>

      {/* At a Glance - Tabbed */}
      <Paper sx={{ p: 2, mb: 0 }}>
        <Box
          sx={{
            px: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 2, md: 3 }
          }}
        >
          <Box>
            <Typography variant="h6">At a Glance</Typography>
            {activeTabConfig?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {activeTabConfig.description}
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
          >
            {activeTabStats.map((stat) => (
              <Chip
                key={`${activeTabConfig.value}-${stat.label}`}
                label={`${Number(stat.value || 0).toLocaleString()} ${stat.label}`}
                size="small"
                color={stat.color || 'default'}
                variant={stat.color ? 'filled' : 'outlined'}
              />
            ))}
            {activeTabConfig && (
              <Button
                size="small"
                variant="contained"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={() => navigate(activeTabConfig.route)}
                sx={{ textTransform: 'none' }}
              >
                {activeTabConfig.actionLabel || `Open ${activeTabConfig.label}`}
              </Button>
            )}
            {hasSecondaryAction && (
              <DevOnly>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    SecondaryActionIcon ? (
                      <SecondaryActionIcon fontSize="small" />
                    ) : undefined
                  }
                  onClick={() => navigate(activeTabConfig.secondaryAction.path)}
                  sx={{ textTransform: 'none' }}
                >
                  {activeTabConfig.secondaryAction.label}
                </Button>
              </DevOnly>
            )}
          </Stack>
        </Box>
        <Tabs
          value={glanceTab}
          onChange={handleGlanceTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mt: 1 }}
        >
          {glanceTabsConfig.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <Tab
                key={tab.value}
                icon={<IconComponent fontSize="small" />}
                iconPosition="start"
                label={tab.label}
                value={tab.value}
              />
            );
          })}
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {glanceTab === 'invoices' && (
            <>
              {isOverviewLoading ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, r) => (
                        <TableRow key={`inv-skel-${r}`}>
                          {Array.from({ length: 6 }).map((_, c) => (
                            <TableCell key={`inv-skel-${r}-${c}`}>
                              <Skeleton variant="text" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : recentInvoices.length === 0 ? (
                <Alert severity="info">No recent invoices found.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentInvoices.map((invoice) => (
                        <TableRow
                          key={invoice.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/financial/invoices/${invoice.id}`)}
                        >
                          <TableCell>{invoice.invoice_number || `#${invoice.id}`}</TableCell>
                          <TableCell>{invoice.client_name || 'Unknown Client'}</TableCell>
                          <TableCell>{formatCurrency(invoice.total_amount)}</TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.status || 'Pending'}
                              color={getInvoiceStatusColor(invoice.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(invoice.created_at)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              aria-label="View invoice"
                              onClick={(e) => { e.stopPropagation(); navigate(`/financial/invoices/${invoice.id}`); }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {glanceTab === 'payments' && (
            <>
              {isOverviewLoading ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, r) => (
                        <TableRow key={`pay-skel-${r}`}>
                          {Array.from({ length: 6 }).map((_, c) => (
                            <TableCell key={`pay-skel-${r}-${c}`}>
                              <Skeleton variant="text" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : recentPayments.length === 0 ? (
                <Alert severity="info">No recent payments found.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Payment #</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentPayments.map((payment) => (
                        <TableRow
                          key={payment.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/financial/payments/${payment.id}`)}
                        >
                          <TableCell>{payment.payment_number || `#${payment.id}`}</TableCell>
                          <TableCell>{payment.client_name || 'Unknown Client'}</TableCell>
                          <TableCell>{formatCurrency(payment.amount)}</TableCell>
                          <TableCell>
                            <Chip
                              label={payment.payment_method || 'Unknown'}
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(payment.created_at)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              aria-label="View payment"
                              onClick={(e) => { e.stopPropagation(); navigate(`/financial/payments/${payment.id}`); }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {glanceTab === 'quotes' && (
            <>
              {isOverviewLoading ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><Skeleton variant="text" width={100} /></TableCell>
                        <TableCell><Skeleton variant="text" width={150} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={100} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, r) => (
                        <TableRow key={`quote-skel-${r}`}>
                          {Array.from({ length: 6 }).map((_, c) => (
                            <TableCell key={`quote-skel-${r}-${c}`}>
                              <Skeleton variant="text" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : recentQuotes.length === 0 ? (
                <Alert severity="info">No quotes found. Create your first quote to get started.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Quote #</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Expiry Date</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentQuotes.map((quote) => (
                        <TableRow
                          key={quote.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/financial/quotes/${quote.id}`)}
                        >
                          <TableCell>{quote.quote_name || `Quote #${quote.quote_id || quote.id}`}</TableCell>
                          <TableCell>{quote.client_name || 'Unknown Client'}</TableCell>
                          <TableCell>{formatCurrency(quote.total_amount || 0)}</TableCell>
                          <TableCell>
                            <Chip
                              label={quote.status || 'Draft'}
                              color={getQuoteStatusColor(quote.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDate(quote.expiration_date)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              aria-label="View quote"
                              onClick={(e) => { e.stopPropagation(); navigate(`/financial/quotes/${quote.id}`); }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {glanceTab === 'discounts' && (
            <>
              {isOverviewLoading ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><Skeleton variant="text" width={150} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={100} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, r) => (
                        <TableRow key={`discount-skel-${r}`}>
                          {Array.from({ length: 6 }).map((_, c) => (
                            <TableCell key={`discount-skel-${r}-${c}`}>
                              <Skeleton variant="text" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : recentDiscounts.length === 0 ? (
                <Alert severity="info">No discounts found. Create your first discount to get started.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Discount Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Available</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentDiscounts.map((discount) => (
                        <TableRow
                          key={discount.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/discounts/${discount.id}`)}
                        >
                          <TableCell>{discount.name || 'Unnamed Discount'}</TableCell>
                          <TableCell>
                            <Chip
                              label={discount.type === '1' ? 'Percentage' : 'Fixed'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {discount.type === '1' ? `${Math.abs(discount.rate)}%` : formatCurrency(Math.abs(discount.rate))}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={discount.is_active ? 'Active' : 'Inactive'}
                              color={discount.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={discount.online ? 'Online' : 'In-Store'}
                              size="small"
                              variant="outlined"
                              color={discount.online ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              aria-label="View discount"
                              onClick={(e) => { e.stopPropagation(); navigate(`/discounts/${discount.id}`); }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {glanceTab === 'vouchers' && (
            <>
              {isOverviewLoading ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><Skeleton variant="text" width={150} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                        <TableCell><Skeleton variant="text" width={100} /></TableCell>
                        <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, r) => (
                        <TableRow key={`voucher-skel-${r}`}>
                          {Array.from({ length: 6 }).map((_, c) => (
                            <TableCell key={`voucher-skel-${r}-${c}`}>
                              <Skeleton variant="text" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : recentVouchers.length === 0 ? (
                <Alert severity="info">No gift vouchers found. Create your first voucher to get started.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Voucher Name</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Valid For</TableCell>
                        <TableCell>Expiry Date</TableCell>
                        <TableCell>Available</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentVouchers.map((voucher) => (
                        <TableRow
                          key={voucher.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/vouchers/${voucher.id}`)}
                        >
                          <TableCell>{voucher.name || 'Gift Voucher'}</TableCell>
                          <TableCell>{formatCurrency(voucher.amount || 0)}</TableCell>
                          <TableCell>{voucher.valid_for ? `${voucher.valid_for} days` : 'No expiry'}</TableCell>
                          <TableCell>{formatDate(voucher.expiry_date)}</TableCell>
                          <TableCell>
                            <Chip
                              label={voucher.online ? 'Online' : 'In-Store'}
                              size="small"
                              variant="outlined"
                              color={voucher.online ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              aria-label="View voucher"
                              onClick={(e) => { e.stopPropagation(); navigate(`/vouchers/${voucher.id}`); }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

// Summary Card Component
const SummaryCard = ({ title, total, subtitle, icon: IconComponent, accent = 'primary', loading = false }) => {
  const theme = useTheme();
  const accentColor = theme.palette[accent]?.main || theme.palette.primary.main;
  const accentBackground = alpha(accentColor, 0.12);

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        height: '100%'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={90} height={32} sx={{ mt: 0.5 }} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 600, mt: 0.5 }}>
              {Number(total || 0).toLocaleString()}
            </Typography>
          )}
          {subtitle && (
            loading ? (
              <Skeleton variant="text" width={110} height={20} sx={{ mt: 1 }} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {subtitle}
              </Typography>
            )
          )}
        </Box>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: accentBackground,
            color: accentColor,
            flexShrink: 0
          }}
        >
          {IconComponent ? <IconComponent fontSize="small" /> : null}
        </Box>
      </Box>
    </Paper>
  );
};

// Helper function to get invoice status color
const getInvoiceStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'overdue':
      return 'error';
    case 'draft':
      return 'default';
    default:
      return 'default';
  }
};

// Helper function to get quote status color
const getQuoteStatusColor = (status) => {
  const statusLower = status?.toLowerCase();
  if (statusLower === 'accepted' || statusLower === 'approved') {
    return 'success';
  } else if (statusLower === 'published' || statusLower === 'sent' || statusLower === 'pending') {
    return 'warning';
  } else if (statusLower === 'rejected' || statusLower === 'expired') {
    return 'error';
  } else if (statusLower === 'draft') {
    return 'default';
  }
  return 'default';
};

export default FinancialDashboard;
